import React, { useEffect, useState, useCallback } from 'react';
import { 
  Key, Search, Filter, Loader, AlertCircle, CheckCircle, 
  Trash2, ChevronRight, 
  Activity, Shield, Mail, CreditCard,
  Smartphone, Zap, Award, ExternalLink,
  Plus, Check, Copy, RefreshCw, Terminal, Code2,
  Lock, Globe, Cpu, Send, Layers, ShieldCheck
} from 'lucide-react';
import api from '../../services/api';
import PageHeader from '../../components/PageHeader';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';

const CODE_EXAMPLES = {
  send: `curl -X POST https://api.waapleads.com/api/client/messages/send \\
  -H "Authorization: Bearer YOUR_API_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "deviceId": "DEVICE_ID",
    "to": "91XXXXXXXXXX",
    "message": "Test request succeeded."
  }'`,
  bulk: `curl -X POST https://api.waapleads.com/api/client/messages/bulk \\
  -H "Authorization: Bearer YOUR_API_TOKEN" \\
  -F "file=@contacts.xlsx" \\
  -F "deviceId=DEVICE_ID" \\
  -F "message=Hello {name}!"`,
  status: `curl https://api.waapleads.com/api/client/devices \\
  -H "Authorization: Bearer YOUR_API_TOKEN"`,
};

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button 
      className={cn(
 "h-8 px-3 text-[13px] font-semibold rounded whitespace-nowrap flex items-center gap-1.5 transition-colors",
 copied ? "bg-[#DCFCE7] text-[#16A34A] border border-[#BBF7D0]" : "bg-white text-[#444444] border border-[#D8DDE6] hover:bg-[#F3F2F2]"
 )}
      onClick={handleCopy}
    >
      {copied ? <Check size={14} /> : <Copy size={14} />}
      {copied ? 'Copied' : 'Copy Snippet'}
    </button>
  );
}

export default function SuperAdminAPIKeys() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [generatingId, setGeneratingId] = useState(null);
  const [newKey, setNewKey] = useState({ companyId: null, key: '' });
  const [activeExample, setActiveExample] = useState('send');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await api.get('/admin/companies', { params: { limit: 200 } });
      setCompanies(res.data.companies || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to sync API key registry.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async (companyId, companyName) => {
    const confirmed = await alertService.confirm(
      'Rotate Security Token?',
      `Existing integrations for "${companyName}" will be terminated immediately. New credentials will be required.`,
      'warning',
      'Rotate Token'
    );

    if (!confirmed) return;

    try {
      setGeneratingId(companyId);
      const res = await api.post(`/admin/companies/${companyId}/api-key`);
      setNewKey({ companyId, key: res.data.apiKey });
      alertService.toast('success', 'Security token successfully rotated');
      load();
    } catch (e) {
      alertService.error('Injection Failed', e.response?.data?.error || 'Failed to generate new key.');
    } finally {
      setGeneratingId(null);
    }
  };

  const handleRevoke = async (companyId, companyName) => {
    const confirmed = await alertService.confirm(
      'Revoke Integration Access?',
      `This will completely disable all external API protocols for "${companyName}".`,
      'danger',
      'Revoke Access'
    );

    if (!confirmed) return;

    try {
      await api.delete(`/admin/api-keys/${companyId}`);
      if (newKey.companyId === companyId) setNewKey({ companyId: null, key: '' });
      alertService.toast('success', 'External protocol link dissolved');
      load();
    } catch (e) {
      alertService.error('Revocation Failed', e.response?.data?.error || 'Failed to revoke key.');
    }
  };

  const filtered = companies.filter(c => 
    (c.companyName || c.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.email || '').toLowerCase().includes(search.toLowerCase())
  );

  const withKeys = filtered.filter(c => c.apiKey);
  const withoutKeys = filtered.filter(c => !c.apiKey);

  return (
    <div className="w-full pb-10 animate-in fade-in slide-in-from-bottom-2 duration-500">
      
      <PageHeader
        title="Integration Registry"
        icon={Key}
        subtitle={`Manage external integrations and master token rotation. Active integrations: ${withKeys.length}`}
        actions={<div className="w-full sm:w-72"><Input icon={Search} className="text-[13px]" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} /></div>}
      />

      {error && (
        <Card className="mb-8 border-[#FC8181] bg-[#FFF5F5] p-4 flex items-center gap-3 text-[#C53030] font-semibold text-[13px] rounded-lg">
          <AlertCircle size={16} /> Connection Error: {error}
        </Card>
      )}

      {/* Security Token Alert */}
      {newKey.key && (
        <Card className="mb-8 p-6 rounded-lg bg-[#E0F2FE] border border-[#BAE6FD] flex flex-col gap-6 animate-in zoom-in-95 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
           <div className="space-y-1.5">
              <h3 className="text-lg font-bold text-[#0176D3] flex items-center gap-2">
                <ShieldCheck size={20} />
                Security Token Generated
              </h3>
              <p className="text-[13px] text-[#444444] max-w-2xl">The integration protocol has been updated. Preserve this signature immediately; it will be permanently masked after this session for security integrity.</p>
           </div>
           <div className="flex flex-col md:flex-row items-center gap-4">
              <code className="w-full flex-1 p-3 rounded-lg bg-white border border-[#E5E5E5] text-[#080707] font-mono text-[14px] break-all selection:bg-[#0176D3] selection:text-white">
                 {newKey.key}
              </code>
              <div className="flex gap-2 w-full md:w-auto">
                 <CopyButton text={newKey.key} />
                 <button 
                   className="h-8 px-4 text-[#444444] text-[13px] font-semibold hover:text-[#080707] transition-colors"
                   onClick={() => setNewKey({ companyId: null, key: '' })}
                 >
                   Dismiss
                 </button>
              </div>
           </div>
        </Card>
      )}

      {/* Registry Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <Card className="p-6 border-[#E5E5E5] bg-white rounded-lg flex items-center gap-4">
           <div className="w-12 h-12 rounded-md bg-[#DCFCE7] text-[#16A34A] flex items-center justify-center">
              <Lock size={24} />
           </div>
           <div>
              <p className="text-[12px] font-semibold text-[#706E6B] uppercase ">Authorized Links</p>
              <div className="text-2xl font-bold text-[#080707] leading-none mt-1">{withKeys.length} <span className="text-[14px] text-[#444444] font-normal">Nodes</span></div>
           </div>
        </Card>
        <Card className="p-6 border-[#E5E5E5] bg-white rounded-lg flex items-center gap-4">
           <div className="w-12 h-12 rounded-md bg-[#F3F2F2] text-[#444444] flex items-center justify-center">
              <Shield size={24} />
           </div>
           <div>
              <p className="text-[12px] font-semibold text-[#706E6B] uppercase ">Restricted Access</p>
              <div className="text-2xl font-bold text-[#080707] leading-none mt-1">{withoutKeys.length} <span className="text-[14px] text-[#444444] font-normal">Closed</span></div>
           </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-12">
        
        {/* Active Integrations */}
        <Card className="p-0 overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.08)] bg-white border-[#E5E5E5] rounded-lg">
           <div className="p-4 border-b border-[#D8DDE6] flex items-center justify-between bg-[#F4F6F9]">
              <div className="space-y-0.5">
                 <h3 className="text-[14px] font-bold text-[#080707] flex items-center gap-2">
                    <Activity size={16} className="text-[#16A34A]" /> Active Integrations
                 </h3>
                 <p className="text-[12px] text-[#706E6B]">Entities with established protocol linkage</p>
              </div>
              <Badge variant="emerald" className="bg-[#DCFCE7] text-[#16A34A] text-[11px] font-bold px-2 py-0.5">SYNC_ONLINE</Badge>
           </div>
           
           <div className="overflow-x-auto">
              {loading ? (
                <div className="py-20 flex flex-col items-center justify-center gap-4 opacity-50">
                   <Loader size={32} className="animate-spin text-[#0176D3]" />
                   <p className="text-[12px] font-semibold text-[#706E6B]">Loading API keys…</p>
                </div>
              ) : withKeys.length === 0 ? (
                <div className="py-20 flex flex-col items-center justify-center text-center gap-4 opacity-50">
                   <div className="w-16 h-16 rounded-md bg-[#F3F2F2] border border-[#D8DDE6] flex items-center justify-center text-[#444444]">
                      <Lock size={24} />
                   </div>
                   <p className="text-[13px] text-[#706E6B] max-w-xs">No active API linkage detected across existing network nodes.</p>
                </div>
              ) : (
                <div className="slds2-table-wrap overflow-x-auto">
                <table className="w-full text-left border-collapse">
                   <thead>
                      <tr className="bg-[#F4F6F9] text-[11px] font-semibold text-[#706E6B] uppercase border-b border-[#D8DDE6]">
                         <th className="px-4 py-2.5 font-semibold">Organization</th>
                         <th className="px-4 py-2.5 font-semibold">Signature Fragment</th>
                         <th className="px-4 py-2.5 font-semibold text-right">Administrative Actions</th>
                      </tr>
                   </thead>
                   <tbody className="divide-y divide-[#D8DDE6]">
                      {withKeys.map(c => (
                         <tr key={c._id} className="hover:bg-[#F7F9FB] transition-colors">
                            <td className="px-4 py-2.5">
                               <div className="space-y-0.5">
                                  <div className="text-[13px] font-bold text-[#080707] truncate max-w-[200px]">{c.companyName || c.name}</div>
                                  <div className="text-[12px] text-[#706E6B]">{c.email}</div>
                               </div>
                            </td>
                            <td className="px-4 py-2.5">
                               <code className="text-[12px] font-mono text-[#080707] bg-[#F4F6F9] px-2 py-1 rounded-sm border border-[#D8DDE6]">
                                  {c.apiKey.slice(0, 8)}…{c.apiKey.slice(-4)}
                               </code>
                            </td>
                            <td className="px-4 py-2.5 text-right">
                               <div className="flex items-center justify-end gap-2">
                                  <button
                                    className="h-8 px-3 rounded whitespace-nowrap bg-white border border-[#C9C9C9] text-[#444444] hover:text-[#080707] hover:bg-[#F3F2F2] text-[13px] font-semibold transition-colors"
                                    onClick={() => handleGenerate(c._id, c.companyName || c.name)}
                                    disabled={generatingId === c._id}
                                  >
                                     {generatingId === c._id ? <Loader size={14} className="animate-spin" /> : 'Rotate Token'}
                                  </button>
                                  <button
                                    className="w-8 h-8 rounded-lg bg-white border border-[#E5E5E5] flex items-center justify-center text-[#E53E3E] hover:bg-[#FFF5F5] transition-colors shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                                    onClick={() => handleRevoke(c._id, c.companyName || c.name)}
                                    title="Revoke Access"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                               </div>
                            </td>
                         </tr>
                      ))}
                   </tbody>
                </table>
                </div>
              )}
           </div>
        </Card>

        {/* Restricted Accounts */}
        <Card className="p-0 overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.08)] bg-white border-[#E5E5E5] rounded-lg">
           <div className="p-4 border-b border-[#D8DDE6] flex items-center justify-between bg-[#F4F6F9]">
              <div className="space-y-0.5">
                 <h3 className="text-[14px] font-bold text-[#080707] flex items-center gap-2">
                    <Shield size={16} className="text-[#444444]" /> Restricted Accounts
                 </h3>
                 <p className="text-[12px] text-[#706E6B]">Entities without external linkage protocols</p>
              </div>
              <Badge variant="rose" className="bg-[#FFF5F5] text-[#C53030] text-[11px] font-bold px-2 py-0.5">LINK_REQUIRED</Badge>
           </div>

           <div className="overflow-x-auto">
              {loading ? (
                <div className="py-20 flex flex-col items-center justify-center gap-4 opacity-50">
                   <Loader size={32} className="animate-spin text-[#0176D3]" />
                   <p className="text-[12px] font-semibold text-[#706E6B]">Syncing Gateway Registrar…</p>
                </div>
              ) : withoutKeys.length === 0 ? (
                <div className="py-20 flex flex-col items-center justify-center text-center gap-4 opacity-50">
                   <div className="w-16 h-16 rounded-md bg-[#E0F2FE] border border-[#BAE6FD] flex items-center justify-center text-[#0176D3]">
                      <Activity size={24} />
                   </div>
                   <p className="text-[13px] text-[#706E6B] max-w-xs">All network entities possess active integration linkage. System fully synchronized.</p>
                </div>
              ) : (
                <div className="slds2-table-wrap overflow-x-auto">
                <table className="w-full text-left border-collapse">
                   <thead>
                      <tr className="bg-[#F4F6F9] text-[11px] font-semibold text-[#706E6B] uppercase border-b border-[#D8DDE6]">
                         <th className="px-4 py-2.5 font-semibold">Organization</th>
                         <th className="px-4 py-2.5 font-semibold">Subscription Tier</th>
                         <th className="px-4 py-2.5 font-semibold text-right">Access</th>
                      </tr>
                   </thead>
                   <tbody className="divide-y divide-[#D8DDE6]">
                      {withoutKeys.map(c => (
                         <tr key={c._id} className="hover:bg-[#F7F9FB] transition-colors">
                            <td className="px-4 py-2.5">
                               <div className="space-y-0.5">
                                  <div className="text-[13px] font-bold text-[#080707] truncate max-w-[200px]">{c.companyName || c.name}</div>
                                  <div className="text-[12px] text-[#706E6B]">{c.email}</div>
                               </div>
                            </td>
                            <td className="px-4 py-2.5">
                               <Badge variant="blue" className="bg-[#E0F2FE] text-[#0176D3] text-[11px] font-bold px-2 py-0.5">
                                  {c.subscriptionPlan?.name || 'TRIAL'}
                               </Badge>
                            </td>
                            <td className="px-4 py-2.5 text-right">
                               <button
                                 className="h-8 px-4 rounded whitespace-nowrap bg-[#0176D3] hover:bg-[#014486] text-white text-[13px] font-semibold transition-colors flex items-center gap-1.5 ml-auto"
                                 onClick={() => handleGenerate(c._id, c.companyName || c.name)}
                                 disabled={generatingId === c._id}
                               >
                                  {generatingId === c._id ? <Loader size={14} className="animate-spin" /> : <><Plus size={14} /> Grant Access</>}
                               </button>
                            </td>
                         </tr>
                      ))}
                   </tbody>
                </table>
                </div>
              )}
           </div>
        </Card>
      </div>

      {/* Integration Manifest */}
      <Card className="mt-8 p-0 overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.08)] bg-white border-[#E5E5E5] rounded-lg">
         <div className="p-6 border-b border-[#D8DDE6] flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 bg-[#F4F6F9]">
            <div className="space-y-1">
               <h3 className="text-xl font-bold text-[#080707] flex items-center gap-2">
                  <Terminal size={20} className="text-[#0176D3]" /> Integration Manifest
               </h3>
               <p className="text-[13px] text-[#706E6B] flex items-center gap-1.5">
                  <Cpu size={14} className="text-[#16A34A]" /> Global external protocol reference and operational standards
               </p>
            </div>
            <div className="flex bg-[#F3F2F2] p-1 rounded-md border border-[#D8DDE6]">
               {[
                 { key: 'send', label: 'MSG_SEND', icon: Send },
                 { key: 'bulk', label: 'BULK_STREAM', icon: Layers },
                 { key: 'status', label: 'NODE_SYNC', icon: Smartphone },
               ].map(ex => (
                 <button
                   key={ex.key}
                   className={cn(
 "flex items-center gap-2 px-4 py-2 rounded whitespace-nowrap text-[13px] font-semibold transition-colors",
 activeExample === ex.key ? 'bg-white text-[#080707] shadow-sm' : 'text-[#444444] hover:text-[#080707]'
 )}
                   onClick={() => setActiveExample(ex.key)}
                 >
                   <ex.icon size={14} /> {ex.label}
                 </button>
               ))}
            </div>
         </div>
         
         <div className="p-6 lg:p-8 space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
               <div className="lg:col-span-1 space-y-8">
                  <div className="space-y-3">
                     <div className="text-[12px] font-semibold text-[#444444] uppercase flex items-center gap-2 pb-2 border-b border-[#D8DDE6]">
                        <ShieldCheck size={16} className="text-[#0176D3]" /> Password
                     </div>
                     <div className="p-4 rounded-md bg-[#F4F6F9] border border-[#D8DDE6] space-y-4">
                        <p className="text-[13px] text-[#080707] leading-relaxed">
                           All outbound uplink requests must possess a Bearer signature:
                        </p>
                        <div className="p-3 rounded-lg bg-white border border-[#E5E5E5] text-[#0176D3] font-mono text-[13px] break-all shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
                           Authorization: Bearer [API_TOKEN]
                        </div>
                     </div>
                  </div>
                  
                  <div className="space-y-3">
                     <div className="text-[12px] font-semibold text-[#444444] uppercase flex items-center gap-2 pb-2 border-b border-[#D8DDE6]">
                        <Cpu size={16} className="text-[#0176D3]" /> System Endpoints
                     </div>
                     <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2 scrollbar-hide">
                        {[
                          ['GET', '/api/client/devices', 'NODE_INDEX'],
                          ['POST', '/api/client/messages/send', 'UNIT_SIGNAL'],
                          ['POST', '/api/client/messages/bulk', 'BULK_INJECT'],
                          ['GET', '/api/client/chats', 'STREAM_REG'],
                          ['GET', '/api/client/invoices', 'FIN_LEDGER'],
                        ].map(([method, path, label]) => (
                           <div key={path} className="flex items-center justify-between p-3 rounded-lg bg-white border border-[#E5E5E5] hover:bg-[#F9F9F9] transition-colors shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
                              <span className={cn(
 "text-[11px] font-bold px-2 py-0.5 rounded-sm border",
 method === 'GET' ? 'bg-[#E0F2FE] text-[#0176D3] border-[#BAE6FD]' : 'bg-[#DCFCE7] text-[#16A34A] border-[#BBF7D0]'
 )}>{method}</span>
                              <span className="text-[12px] text-[#444444] font-mono">{path}</span>
                           </div>
                        ))}
                     </div>
                  </div>
               </div>

               <div className="lg:col-span-2">
                  <div className="rounded-lg overflow-hidden bg-white border border-[#E5E5E5] shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
                     <div className="flex items-center justify-between px-4 py-3 bg-[#F4F6F9] border-b border-[#D8DDE6]">
                        <div className="flex gap-2">
                           <div className="w-3 h-3 rounded-full bg-[#E53E3E]" />
                           <div className="w-3 h-3 rounded-full bg-[#D69E2E]" />
                           <div className="w-3 h-3 rounded-full bg-[#38A169]" />
                        </div>
                        <div className="text-[12px] font-semibold text-[#706E6B] font-mono">INTEGRATION_SNIPPET.SH</div>
                        <CopyButton text={CODE_EXAMPLES[activeExample]} />
                     </div>
                     <pre className="p-6 text-[13px] font-mono text-[#080707] leading-relaxed overflow-x-auto bg-[#FAFAFA]">
                        {CODE_EXAMPLES[activeExample]}
                     </pre>
                  </div>
               </div>
            </div>
         </div>

         <div className="p-4 bg-[#F4F6F9] border-t border-[#D8DDE6] flex items-center justify-between text-[11px] font-semibold text-[#706E6B]">
            <div className="flex items-center gap-2">
               <Globe size={14} className="text-[#0176D3]" /> <span>API version 4.2</span>
            </div>
            <div className="flex items-center gap-2">
               <div className="w-2 h-2 rounded-full bg-[#16A34A]" /> 
               Data Uplink Status: <span>Encrypted / Online</span>
            </div>
         </div>
      </Card>
    </div>
  );
}
